import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Zap,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Globe,
  Database,
  Lock,
  Clock,
  XCircle,
  ChevronRight,
  ChevronDown,
  Link2,
  Shield,
  FileCode,
  Info,
  Ban,
  Hash,
  Send,
  Radio,
  Eye,
  Activity,
  Key,
  Users,
  Layers,
  Settings,
  FileText,
  GitBranch
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Capabilities & Effects Panel - Constitutional Layer
 * 
 * CAPABILITY PHILOSOPHY:
 * A capability must satisfy ALL of these criteria:
 * 1. Represents side-effects or epistemic authority
 * 2. Can be denied by runtime profile
 * 3. Is auditable
 * 4. Its absence has semantic consequences, not just runtime errors
 * 5. Is meaningful to reason about in explanations
 * 
 * We do NOT add: random helpers, utils, formatting, internal computation
 * We add: AUTHORITY SURFACES
 */

// ============================================================================
// CANONICAL CAPABILITY REGISTRY - Domain-Organized
// ============================================================================

const CAPABILITY_DOMAINS = [
  {
    id: 'time',
    name: 'Time & Temporal',
    icon: Clock,
    color: 'text-cyan-400',
    description: 'Authority over time, scheduling, and temporal reasoning',
    capabilities: [
      { 
        id: 'Clock.Now', 
        name: 'Clock.Now',
        description: 'Read current wall-clock time',
        runtimes: ['simulation', 'offline_browser', 'online_browser', 'worker', 'server'],
        deterministic: false,
        risk: 'low'
      },
      { 
        id: 'Clock.Monotonic', 
        name: 'Clock.Monotonic',
        description: 'Guaranteed monotonic time source (for ordering)',
        runtimes: ['offline_browser', 'online_browser', 'worker', 'server'],
        deterministic: true,
        risk: 'low'
      },
      { 
        id: 'Clock.Sleep', 
        name: 'Clock.Sleep',
        description: 'Authority to delay/pause execution',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Clock.Schedule', 
        name: 'Clock.Schedule',
        description: 'Register future execution (cron/alarms)',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
    ]
  },
  {
    id: 'net',
    name: 'External Communication',
    icon: Globe,
    color: 'text-blue-400',
    description: 'Authority over network and external system communication',
    capabilities: [
      { 
        id: 'Net.HTTP.Outbound', 
        name: 'Net.HTTP.Outbound',
        description: 'Initiate outbound HTTP requests',
        runtimes: ['online_browser', 'worker', 'server'],
        deterministic: false,
        risk: 'high'
      },
      { 
        id: 'Net.HTTP.Inbound', 
        name: 'Net.HTTP.Inbound',
        description: 'Receive external HTTP triggers',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'high'
      },
      { 
        id: 'Net.WebSocket', 
        name: 'Net.WebSocket',
        description: 'Bidirectional WebSocket connections',
        runtimes: ['online_browser', 'worker', 'server'],
        deterministic: false,
        risk: 'high'
      },
      { 
        id: 'Net.Webhook.Emit', 
        name: 'Net.Webhook.Emit',
        description: 'One-way outbound signaling (fire-and-forget)',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Net.Stream.Subscribe', 
        name: 'Net.Stream.Subscribe',
        description: 'Long-lived external data stream subscription',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'high'
      },
    ]
  },
  {
    id: 'storage',
    name: 'Storage & Persistence',
    icon: Database,
    color: 'text-emerald-400',
    description: 'Authority over data persistence with semantic guarantees',
    capabilities: [
      { 
        id: 'Storage.ReadOnly', 
        name: 'Storage.ReadOnly',
        description: 'Read without write authority',
        runtimes: ['simulation', 'offline_browser', 'online_browser', 'worker', 'server'],
        deterministic: true,
        risk: 'low'
      },
      { 
        id: 'Storage.Mutable', 
        name: 'Storage.Mutable',
        description: 'Read and overwrite/update authority',
        runtimes: ['offline_browser', 'online_browser', 'worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Storage.AppendOnly', 
        name: 'Storage.AppendOnly',
        description: 'Write-once logs / event journals',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'low'
      },
      { 
        id: 'Storage.Transactional', 
        name: 'Storage.Transactional',
        description: 'Atomic multi-write authority',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Storage.KV', 
        name: 'Storage.KV',
        description: 'Key-value store access',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Storage.DO', 
        name: 'Storage.DO',
        description: 'Durable Objects (stateful edge storage)',
        runtimes: ['worker'],
        deterministic: false,
        risk: 'high'
      },
      { 
        id: 'Storage.Local', 
        name: 'Storage.Local',
        description: 'Browser local storage',
        runtimes: ['offline_browser', 'online_browser'],
        deterministic: false,
        risk: 'low'
      },
    ]
  },
  {
    id: 'identity',
    name: 'Identity & Trust',
    icon: Lock,
    color: 'text-yellow-400',
    description: 'Authority over identity, authentication, and trust relationships',
    capabilities: [
      { 
        id: 'Identity.Current', 
        name: 'Identity.Current',
        description: 'Access current invoking principal',
        runtimes: ['online_browser', 'worker', 'server'],
        deterministic: false,
        risk: 'low'
      },
      { 
        id: 'Identity.Impersonate', 
        name: 'Identity.Impersonate',
        description: 'Act on behalf of another principal',
        runtimes: ['server'],
        deterministic: false,
        risk: 'critical'
      },
      { 
        id: 'Identity.Delegate', 
        name: 'Identity.Delegate',
        description: 'Issue limited authority to another actor',
        runtimes: ['server'],
        deterministic: false,
        risk: 'high'
      },
      { 
        id: 'Trust.VerifySignature', 
        name: 'Trust.VerifySignature',
        description: 'Cryptographic signature verification',
        runtimes: ['offline_browser', 'online_browser', 'worker', 'server'],
        deterministic: true,
        risk: 'low'
      },
      { 
        id: 'Trust.Sign', 
        name: 'Trust.Sign',
        description: 'Cryptographic signing authority',
        runtimes: ['worker', 'server'],
        deterministic: true,
        risk: 'high'
      },
      { 
        id: 'Trust.EstablishSession', 
        name: 'Trust.EstablishSession',
        description: 'Create stateful trust relationship',
        runtimes: ['online_browser', 'worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Auth.OAuth', 
        name: 'Auth.OAuth',
        description: 'OAuth authentication flow',
        runtimes: ['online_browser', 'server'],
        deterministic: false,
        risk: 'medium'
      },
    ]
  },
  {
    id: 'queue',
    name: 'Messaging & Orchestration',
    icon: Layers,
    color: 'text-orange-400',
    description: 'Authority over queues, messaging, and multi-machine coordination',
    capabilities: [
      { 
        id: 'Queue.Enqueue', 
        name: 'Queue.Enqueue',
        description: 'Add message to queue',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Queue.Dequeue', 
        name: 'Queue.Dequeue',
        description: 'Consume message from queue',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Queue.Delay', 
        name: 'Queue.Delay',
        description: 'Delayed message delivery',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Queue.DeadLetter', 
        name: 'Queue.DeadLetter',
        description: 'Route failed messages to dead letter queue',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'low'
      },
      { 
        id: 'Orchestrator.Spawn', 
        name: 'Orchestrator.Spawn',
        description: 'Create child machine instance',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'high'
      },
      { 
        id: 'Orchestrator.Signal', 
        name: 'Orchestrator.Signal',
        description: 'Send event to another machine',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Orchestrator.Join', 
        name: 'Orchestrator.Join',
        description: 'Wait for child machine completion',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
    ]
  },
  {
    id: 'observe',
    name: 'Observability & Explanation',
    icon: Eye,
    color: 'text-purple-400',
    description: 'Authority over logging, metrics, tracing, and proof emission',
    capabilities: [
      { 
        id: 'Observe.Log', 
        name: 'Observe.Log',
        description: 'Structured logging',
        runtimes: ['simulation', 'offline_browser', 'online_browser', 'worker', 'server'],
        deterministic: true,
        risk: 'low'
      },
      { 
        id: 'Observe.Metric.Emit', 
        name: 'Observe.Metric.Emit',
        description: 'Emit metrics/telemetry',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'low'
      },
      { 
        id: 'Observe.Trace.Span', 
        name: 'Observe.Trace.Span',
        description: 'Create distributed trace span',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'low'
      },
      { 
        id: 'Explain.EmitProof', 
        name: 'Explain.EmitProof',
        description: 'Emit audit proof or explanation certificate',
        runtimes: ['simulation', 'worker', 'server'],
        deterministic: true,
        risk: 'low'
      },
    ]
  },
  {
    id: 'env',
    name: 'Environment & Platform',
    icon: Settings,
    color: 'text-gray-400',
    description: 'Authority over environment access and platform configuration',
    capabilities: [
      { 
        id: 'Env.Read', 
        name: 'Env.Read',
        description: 'Read environment variables',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'medium'
      },
      { 
        id: 'Env.Secret.Read', 
        name: 'Env.Secret.Read',
        description: 'Read secret/credential values',
        runtimes: ['worker', 'server'],
        deterministic: false,
        risk: 'critical'
      },
      { 
        id: 'Env.FeatureFlag', 
        name: 'Env.FeatureFlag',
        description: 'Read feature flag state',
        runtimes: ['offline_browser', 'online_browser', 'worker', 'server'],
        deterministic: false,
        risk: 'low'
      },
    ]
  },
];

// Flatten for quick lookup
const ALL_CAPABILITIES = CAPABILITY_DOMAINS.flatMap(d => 
  d.capabilities.map(c => ({ ...c, domain: d.id, domainName: d.name, domainIcon: d.icon, domainColor: d.color }))
);

// Execution types for effects
const EXECUTION_TYPES = [
  { id: 'sync', label: 'Synchronous', description: 'Blocks until complete' },
  { id: 'async', label: 'Asynchronous', description: 'Returns promise' },
  { id: 'deferred', label: 'Deferred', description: 'Queued for later execution' },
];

// Determinism declarations
const DETERMINISM_TYPES = [
  { id: 'deterministic', label: 'Deterministic', description: 'Same input → same output' },
  { id: 'environment', label: 'Environment-Dependent', description: 'Depends on external state' },
];

// Risk levels with visual treatment
const RISK_CONFIG = {
  low: { label: 'LOW', color: 'text-green-400 bg-green-500/10 border-green-500/30' },
  medium: { label: 'MED', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
  high: { label: 'HIGH', color: 'text-orange-400 bg-orange-500/10 border-orange-500/30' },
  critical: { label: 'CRIT', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
};

// Validation error codes
const ERROR_CODES = {
  CAP_UNUSED: { code: 'CAP_UNUSED', severity: 'warning', message: 'Capability declared but never used' },
  CAP_UNAVAILABLE: { code: 'CAP_UNAVAILABLE', severity: 'error', message: 'Capability unavailable in runtime' },
  CAP_REFERENCED: { code: 'CAP_REFERENCED', severity: 'error', message: 'Cannot remove: referenced by effect' },
  CAP_OVERLY_BROAD: { code: 'CAP_OVERLY_BROAD', severity: 'warning', message: 'Consider narrowing authority scope' },
  EFFECT_INVALID_CAP: { code: 'EFFECT_INVALID_CAP', severity: 'error', message: 'Effect requires unavailable capability' },
  EFFECT_UNREACHABLE: { code: 'EFFECT_UNREACHABLE', severity: 'warning', message: 'Effect defined but never invoked' },
  EFFECT_NAME_CONFLICT: { code: 'EFFECT_NAME_CONFLICT', severity: 'error', message: 'Effect name already exists' },
};

// ============================================================================
// CAPABILITY REGISTRY COMPONENT
// ============================================================================

const CapabilityRegistry = ({ 
  machine, 
  runtimeProfile,
  declaredCapabilities,
  onAddCapability,
  onRemoveCapability,
  onSelectCapability,
  selectedCapability,
  onHighlightEffects
}) => {
  const [addingCapability, setAddingCapability] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [selectedDomain, setSelectedDomain] = useState(null);

  // Compute capability statuses
  const capabilityStatuses = useMemo(() => {
    const statuses = {};
    
    declaredCapabilities.forEach(capId => {
      const capDef = ALL_CAPABILITIES.find(c => c.id === capId);
      const effectsUsingIt = getEffectsUsingCapability(machine, capId);
      const isAvailableInRuntime = capDef?.runtimes?.includes(runtimeProfile) ?? false;
      
      statuses[capId] = {
        capId,
        definition: capDef,
        effectCount: effectsUsingIt.length,
        effects: effectsUsingIt,
        status: !isAvailableInRuntime 
          ? 'unavailable' 
          : effectsUsingIt.length === 0 
            ? 'unused' 
            : 'used',
        isAvailableInRuntime
      };
    });
    
    return statuses;
  }, [machine, declaredCapabilities, runtimeProfile]);

  // Group declared capabilities by domain
  const declaredByDomain = useMemo(() => {
    const grouped = {};
    declaredCapabilities.forEach(capId => {
      const cap = ALL_CAPABILITIES.find(c => c.id === capId);
      if (cap) {
        if (!grouped[cap.domain]) grouped[cap.domain] = [];
        grouped[cap.domain].push(capId);
      }
    });
    return grouped;
  }, [declaredCapabilities]);

  const handleRemoveCapability = (capId) => {
    const status = capabilityStatuses[capId];
    if (status?.effectCount > 0) return;
    onRemoveCapability(capId);
  };

  const toggleDomain = (domainId) => {
    setExpandedDomains(prev => ({ ...prev, [domainId]: !prev[domainId] }));
  };

  // Count issues
  const issueCount = Object.values(capabilityStatuses).filter(
    s => s.status === 'unavailable' || s.status === 'unused'
  ).length;

  return (
    <div className="border-b border-white/10">
      {/* Header */}
      <div className="px-4 py-3 bg-black/30 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Shield className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-semibold">Capability Registry</span>
          <Badge variant="outline" className="text-[9px] h-4 px-1.5">
            {declaredCapabilities.length} declared
          </Badge>
          {issueCount > 0 && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 text-yellow-400 border-yellow-500/30">
              {issueCount} issue{issueCount !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs"
          onClick={() => setAddingCapability(true)}
        >
          <Plus className="w-3 h-3 mr-1" />
          Declare
        </Button>
      </div>

      {/* Add Capability Panel (Domain-Grouped) */}
      {addingCapability && (
        <div className="border-b border-white/10 bg-black/20">
          <div className="px-4 py-2 flex items-center justify-between border-b border-white/10">
            <span className="text-xs font-medium text-gray-400">Select Capability to Declare</span>
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setAddingCapability(false)}>
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
          <ScrollArea className="max-h-64">
            <div className="p-2">
              {CAPABILITY_DOMAINS.map(domain => {
                const Icon = domain.icon;
                const available = domain.capabilities.filter(c => !declaredCapabilities.includes(c.id));
                if (available.length === 0) return null;
                
                const isExpanded = expandedDomains[domain.id] ?? false;
                
                return (
                  <div key={domain.id} className="mb-1">
                    <button
                      onClick={() => toggleDomain(domain.id)}
                      className="w-full flex items-center justify-between p-2 rounded hover:bg-white/5"
                    >
                      <div className="flex items-center space-x-2">
                        <Icon className={cn("w-4 h-4", domain.color)} />
                        <span className="text-xs font-medium">{domain.name}</span>
                        <Badge variant="outline" className="text-[8px] h-3 px-1">
                          {available.length}
                        </Badge>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-3 h-3 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-3 h-3 text-gray-500" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-4 pl-2 border-l border-white/10 space-y-1 py-1">
                        {available.map(cap => {
                          const isAvailable = cap.runtimes.includes(runtimeProfile);
                          const risk = RISK_CONFIG[cap.risk];
                          return (
                            <button
                              key={cap.id}
                              onClick={() => {
                                onAddCapability(cap.id);
                                setAddingCapability(false);
                              }}
                              className="w-full flex items-center justify-between p-2 rounded hover:bg-white/5 text-left group"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs font-mono">{cap.name}</span>
                                  {!isAvailable && (
                                    <Badge variant="outline" className="text-[7px] h-3 px-1 text-yellow-500 border-yellow-500/30">
                                      !{runtimeProfile}
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className={cn("text-[7px] h-3 px-1", risk.color)}>
                                    {risk.label}
                                  </Badge>
                                </div>
                                <div className="text-[10px] text-gray-500 truncate">{cap.description}</div>
                              </div>
                              <Plus className="w-3 h-3 text-gray-500 opacity-0 group-hover:opacity-100" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Declared Capabilities List */}
      <ScrollArea className="max-h-40">
        <div className="p-2">
          {declaredCapabilities.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-4 border border-dashed border-white/10 rounded">
              <Shield className="w-6 h-6 mx-auto mb-2 opacity-30" />
              No capabilities declared. Effects cannot execute without authority.
            </div>
          ) : (
            // Group by domain
            Object.entries(declaredByDomain).map(([domainId, caps]) => {
              const domain = CAPABILITY_DOMAINS.find(d => d.id === domainId);
              if (!domain) return null;
              const Icon = domain.icon;
              
              return (
                <div key={domainId} className="mb-2">
                  <div className="flex items-center space-x-1 px-2 py-1 text-[10px] text-gray-500">
                    <Icon className={cn("w-3 h-3", domain.color)} />
                    <span className="uppercase tracking-wider">{domain.name}</span>
                  </div>
                  <div className="space-y-1">
                    {caps.map(capId => {
                      const status = capabilityStatuses[capId];
                      const capDef = status?.definition;
                      const risk = RISK_CONFIG[capDef?.risk || 'low'];
                      const isSelected = selectedCapability === capId;
                      
                      return (
                        <div
                          key={capId}
                          onClick={() => {
                            onSelectCapability(isSelected ? null : capId);
                            if (!isSelected && status?.effects) {
                              onHighlightEffects(status.effects);
                            }
                          }}
                          className={cn(
                            "flex items-center justify-between px-2 py-1.5 rounded cursor-pointer transition-all",
                            isSelected ? "bg-blue-500/20 ring-1 ring-blue-500/50" : "hover:bg-white/5"
                          )}
                        >
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <span className="text-xs font-mono truncate">{capId.split('.').pop()}</span>
                            <Badge variant="outline" className={cn("text-[7px] h-3 px-1", risk.color)}>
                              {risk.label}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center space-x-1.5">
                            {status?.status === 'unavailable' && (
                              <Badge variant="outline" className="text-[8px] h-4 px-1 text-red-400 border-red-500/30 bg-red-500/10">
                                <Ban className="w-2 h-2 mr-0.5" />
                                DENIED
                              </Badge>
                            )}
                            {status?.status === 'unused' && (
                              <Badge variant="outline" className="text-[8px] h-4 px-1 text-yellow-400 border-yellow-500/30">
                                UNUSED
                              </Badge>
                            )}
                            {status?.status === 'used' && (
                              <Badge variant="outline" className="text-[8px] h-4 px-1 text-green-400 border-green-500/30">
                                {status.effectCount}×
                              </Badge>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-4 w-4 p-0",
                                status?.effectCount > 0 ? "text-gray-700 cursor-not-allowed" : "text-gray-500 hover:text-red-400"
                              )}
                              onClick={(e) => { e.stopPropagation(); handleRemoveCapability(capId); }}
                              disabled={status?.effectCount > 0}
                            >
                              <Trash2 className="w-2.5 h-2.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Validation Warnings */}
      {issueCount > 0 && (
        <div className="px-3 py-2 bg-yellow-500/5 border-t border-yellow-500/20">
          <div className="text-[9px] text-yellow-400 font-medium mb-1">AUTHORITY ISSUES</div>
          {Object.values(capabilityStatuses).filter(s => s.status === 'unavailable').slice(0, 3).map(s => (
            <ValidationIssue 
              key={`unavail-${s.capId}`}
              code={ERROR_CODES.CAP_UNAVAILABLE.code}
              message={`${s.capId}: denied in ${runtimeProfile}`}
              severity="error"
            />
          ))}
          {Object.values(capabilityStatuses).filter(s => s.status === 'unused').slice(0, 2).map(s => (
            <ValidationIssue 
              key={`unused-${s.capId}`}
              code={ERROR_CODES.CAP_UNUSED.code}
              message={`${s.capId}: declared but unused`}
              severity="warning"
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// EFFECT REGISTRY COMPONENT
// ============================================================================

const EffectRegistry = ({
  machine,
  declaredCapabilities,
  runtimeProfile,
  selectedEffect,
  onSelectEffect,
  onHighlightStates,
  onCreateEffect,
  onUpdateEffect,
  onDeleteEffect
}) => {
  const [isCreating, setIsCreating] = useState(false);

  // Compute effect data with validation
  const effectsData = useMemo(() => {
    if (!machine?.effects) return [];
    
    return Object.entries(machine.effects).map(([name, effect]) => {
      const invocations = getEffectInvocations(machine, name);
      const requiredCaps = effect.required_capabilities?.map(c => c.name || c) || [];
      const missingCaps = requiredCaps.filter(c => !declaredCapabilities.includes(c));
      const unavailableCaps = requiredCaps.filter(c => {
        const capDef = ALL_CAPABILITIES.find(cap => cap.id === c);
        return capDef && !capDef.runtimes.includes(runtimeProfile);
      });
      
      let status = 'valid';
      if (missingCaps.length > 0 || unavailableCaps.length > 0) {
        status = 'invalid';
      } else if (invocations.length === 0) {
        status = 'unreachable';
      }
      
      return {
        name,
        ...effect,
        invocations,
        invocationCount: invocations.length,
        requiredCaps,
        missingCaps,
        unavailableCaps,
        status
      };
    });
  }, [machine, declaredCapabilities, runtimeProfile]);

  const validCount = effectsData.filter(e => e.status === 'valid').length;
  const invalidCount = effectsData.filter(e => e.status === 'invalid').length;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-4 py-3 bg-black/30 border-b border-white/10 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2">
          <Zap className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-semibold">Effect Registry</span>
          <Badge variant="outline" className="text-[9px] h-4 px-1.5">
            {effectsData.length} defined
          </Badge>
          {invalidCount > 0 && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 text-red-400 border-red-500/30">
              {invalidCount} blocked
            </Badge>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs"
          onClick={() => setIsCreating(true)}
        >
          <Plus className="w-3 h-3 mr-1" />
          New Effect
        </Button>
      </div>

      {/* Two-column layout: List + Editor */}
      <div className="flex-1 flex min-h-0">
        {/* Effect List (Left) */}
        <div className="w-56 border-r border-white/10 flex flex-col">
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {effectsData.length === 0 && !isCreating ? (
                <div className="text-xs text-gray-500 text-center py-6 px-2">
                  <Zap className="w-6 h-6 mx-auto mb-2 opacity-30" />
                  No effects defined.
                  <div className="text-[10px] text-gray-600 mt-1">
                    Effects are named authority units.
                  </div>
                </div>
              ) : (
                effectsData.map(effect => (
                  <EffectListItem
                    key={effect.name}
                    effect={effect}
                    isSelected={selectedEffect === effect.name}
                    onClick={() => {
                      onSelectEffect(effect.name);
                      if (effect.invocations.length > 0) {
                        onHighlightStates(effect.invocations.map(i => i.stateId));
                      }
                    }}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Effect Editor (Right) */}
        <div className="flex-1 min-w-0">
          {isCreating ? (
            <EffectCreator
              declaredCapabilities={declaredCapabilities}
              runtimeProfile={runtimeProfile}
              existingEffectNames={effectsData.map(e => e.name)}
              onCancel={() => setIsCreating(false)}
              onCreate={(effectDef) => {
                onCreateEffect(effectDef);
                setIsCreating(false);
              }}
            />
          ) : selectedEffect ? (
            <EffectEditor
              effect={effectsData.find(e => e.name === selectedEffect)}
              declaredCapabilities={declaredCapabilities}
              runtimeProfile={runtimeProfile}
              onUpdate={onUpdateEffect}
              onDelete={() => {
                onDeleteEffect(selectedEffect);
                onSelectEffect(null);
              }}
              onNavigateToState={(stateId) => onHighlightStates([stateId])}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
              <div className="text-center">
                <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <div>Select effect to inspect</div>
                <div className="text-[10px] text-gray-600 mt-1">or create a new one</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// EFFECT LIST ITEM
// ============================================================================

const EffectListItem = ({ effect, isSelected, onClick }) => {
  const statusConfig = {
    valid: { icon: CheckCircle, color: 'text-green-400' },
    invalid: { icon: XCircle, color: 'text-red-400' },
    unreachable: { icon: AlertTriangle, color: 'text-yellow-400' },
  };
  
  const config = statusConfig[effect.status] || statusConfig.valid;
  const StatusIcon = config.icon;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-2 rounded transition-all",
        isSelected ? "bg-blue-500/20 ring-1 ring-blue-500/50" : "hover:bg-white/5"
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <StatusIcon className={cn("w-3 h-3 flex-shrink-0", config.color)} />
          <span className="text-xs font-mono truncate">{effect.name}</span>
        </div>
        <span className={cn(
          "text-[9px]",
          effect.invocationCount > 0 ? "text-green-400" : "text-yellow-400"
        )}>
          {effect.invocationCount}×
        </span>
      </div>
      
      {/* Capability pills */}
      <div className="flex items-center space-x-0.5 mt-1 ml-5">
        {effect.requiredCaps.slice(0, 4).map(capId => {
          const capDef = ALL_CAPABILITIES.find(c => c.id === capId);
          const isBroken = effect.missingCaps.includes(capId) || effect.unavailableCaps.includes(capId);
          return (
            <div
              key={capId}
              className={cn(
                "w-1.5 h-1.5 rounded-full",
                isBroken ? "bg-red-400" : "bg-gray-500"
              )}
              title={capId}
            />
          );
        })}
      </div>
    </button>
  );
};

// ============================================================================
// EFFECT CREATOR
// ============================================================================

const EffectCreator = ({ 
  declaredCapabilities, 
  runtimeProfile,
  existingEffectNames,
  onCancel, 
  onCreate 
}) => {
  const [name, setName] = useState('');
  const [selectedCaps, setSelectedCaps] = useState([]);
  const [inputType, setInputType] = useState('void');
  const [outputType, setOutputType] = useState('void');
  const [failureType, setFailureType] = useState('Error');
  const [executionType, setExecutionType] = useState('async');
  const [determinism, setDeterminism] = useState('environment');
  const [errors, setErrors] = useState({});

  // Group declared capabilities by domain for display
  const capsByDomain = useMemo(() => {
    const grouped = {};
    declaredCapabilities.forEach(capId => {
      const cap = ALL_CAPABILITIES.find(c => c.id === capId);
      if (cap) {
        if (!grouped[cap.domain]) grouped[cap.domain] = { domain: CAPABILITY_DOMAINS.find(d => d.id === cap.domain), caps: [] };
        grouped[cap.domain].caps.push(cap);
      }
    });
    return Object.values(grouped);
  }, [declaredCapabilities]);

  const validate = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Effect name required';
    } else if (!/^[A-Z][A-Za-z0-9_]*$/.test(name)) {
      newErrors.name = 'Must be PascalCase';
    } else if (existingEffectNames.includes(name)) {
      newErrors.name = ERROR_CODES.EFFECT_NAME_CONFLICT.message;
    }
    
    if (selectedCaps.length === 0) {
      newErrors.caps = 'At least one capability required (effects without authority cannot execute)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    if (!validate()) return;
    
    onCreate({
      name,
      input_type: inputType,
      output_type: outputType,
      failure_type: failureType,
      execution_type: executionType,
      determinism,
      required_capabilities: selectedCaps.map(c => ({ name: c }))
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-2 border-b border-white/10 bg-green-500/5">
        <div className="text-xs font-semibold text-green-400">DEFINE NEW EFFECT</div>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Effect Name */}
          <div>
            <label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
              Effect Name <span className="text-red-400">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="FetchUserData"
              className={cn("h-8 mt-1 font-mono text-sm bg-black/30", errors.name && "border-red-500/50")}
            />
            {errors.name && <div className="text-[10px] text-red-400 mt-1">{errors.name}</div>}
          </div>

          {/* Required Capabilities - Grouped by Domain */}
          <div>
            <label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
              Required Capabilities <span className="text-red-400">*</span>
            </label>
            {declaredCapabilities.length === 0 ? (
              <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                No capabilities declared. Declare capabilities first before creating effects.
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {capsByDomain.map(({ domain, caps }) => {
                  const Icon = domain.icon;
                  return (
                    <div key={domain.id}>
                      <div className="flex items-center space-x-1 text-[9px] text-gray-500 mb-1">
                        <Icon className={cn("w-3 h-3", domain.color)} />
                        <span>{domain.name}</span>
                      </div>
                      <div className="space-y-1">
                        {caps.map(cap => {
                          const isSelected = selectedCaps.includes(cap.id);
                          const isAvailable = cap.runtimes.includes(runtimeProfile);
                          const risk = RISK_CONFIG[cap.risk];
                          return (
                            <button
                              key={cap.id}
                              onClick={() => setSelectedCaps(prev => 
                                isSelected ? prev.filter(c => c !== cap.id) : [...prev, cap.id]
                              )}
                              className={cn(
                                "w-full flex items-center justify-between p-2 rounded border transition-all text-left",
                                isSelected ? "bg-blue-500/20 border-blue-500/50" : "border-white/10 hover:border-white/20"
                              )}
                            >
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-mono">{cap.name}</span>
                                <Badge variant="outline" className={cn("text-[7px] h-3 px-1", risk.color)}>{risk.label}</Badge>
                              </div>
                              <div className="flex items-center space-x-1">
                                {!isAvailable && (
                                  <Badge variant="outline" className="text-[7px] h-3 px-1 text-yellow-500 border-yellow-500/30">
                                    !{runtimeProfile}
                                  </Badge>
                                )}
                                {isSelected && <CheckCircle className="w-3 h-3 text-blue-400" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {errors.caps && <div className="text-[10px] text-red-400 mt-1">{errors.caps}</div>}
          </div>

          {/* I/O Contract */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] font-medium text-gray-500 uppercase">Input</label>
              <Select value={inputType} onValueChange={setInputType}>
                <SelectTrigger className="h-7 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="void">void</SelectItem>
                  <SelectItem value="string">string</SelectItem>
                  <SelectItem value="number">number</SelectItem>
                  <SelectItem value="object">object</SelectItem>
                  <SelectItem value="any">any</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] font-medium text-gray-500 uppercase">Output</label>
              <Select value={outputType} onValueChange={setOutputType}>
                <SelectTrigger className="h-7 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="void">void</SelectItem>
                  <SelectItem value="string">string</SelectItem>
                  <SelectItem value="number">number</SelectItem>
                  <SelectItem value="object">object</SelectItem>
                  <SelectItem value="any">any</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] font-medium text-gray-500 uppercase">Failure</label>
              <Select value={failureType} onValueChange={setFailureType}>
                <SelectTrigger className="h-7 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Error">Error</SelectItem>
                  <SelectItem value="NetworkError">NetworkError</SelectItem>
                  <SelectItem value="AuthError">AuthError</SelectItem>
                  <SelectItem value="TimeoutError">TimeoutError</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Execution Semantics */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] font-medium text-gray-500 uppercase">Execution</label>
              <Select value={executionType} onValueChange={setExecutionType}>
                <SelectTrigger className="h-7 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXECUTION_TYPES.map(t => (<SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] font-medium text-gray-500 uppercase">Determinism</label>
              <Select value={determinism} onValueChange={setDeterminism}>
                <SelectTrigger className="h-7 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DETERMINISM_TYPES.map(t => (<SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </ScrollArea>

      <div className="px-4 py-2 border-t border-white/10 flex justify-end space-x-2">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={handleCreate}>
          Create Effect
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// EFFECT EDITOR
// ============================================================================

const EffectEditor = ({ effect, declaredCapabilities, runtimeProfile, onUpdate, onDelete, onNavigateToState }) => {
  if (!effect) return null;
  const isReferenced = effect.invocationCount > 0;

  return (
    <div className="h-full flex flex-col">
      <div className={cn(
        "px-4 py-2 border-b border-white/10 flex items-center justify-between",
        effect.status === 'invalid' && "bg-red-500/5",
        effect.status === 'unreachable' && "bg-yellow-500/5"
      )}>
        <div className="flex items-center space-x-2">
          <Zap className={cn("w-4 h-4", effect.status === 'valid' ? "text-green-400" : effect.status === 'invalid' ? "text-red-400" : "text-yellow-400")} />
          <span className="font-mono text-sm font-semibold">{effect.name}</span>
          <Badge variant="outline" className={cn(
            "text-[8px] h-4",
            effect.status === 'valid' && "text-green-400 border-green-500/30",
            effect.status === 'invalid' && "text-red-400 border-red-500/30",
            effect.status === 'unreachable' && "text-yellow-400 border-yellow-500/30"
          )}>
            {effect.status.toUpperCase()}
          </Badge>
        </div>
        <Button
          variant="ghost" size="sm" className="h-6 text-xs text-red-400 hover:text-red-300"
          onClick={onDelete} disabled={isReferenced}
          title={isReferenced ? 'Cannot delete: referenced' : 'Delete'}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Required Capabilities */}
          <Section title="Authority Requirements">
            {effect.requiredCaps.length === 0 ? (
              <div className="text-xs text-yellow-400 p-2 bg-yellow-500/10 rounded border border-yellow-500/20">
                No capabilities. This effect has no authority.
              </div>
            ) : (
              <div className="space-y-1">
                {effect.requiredCaps.map(capId => {
                  const capDef = ALL_CAPABILITIES.find(c => c.id === capId);
                  const isMissing = effect.missingCaps.includes(capId);
                  const isUnavailable = effect.unavailableCaps.includes(capId);
                  const risk = RISK_CONFIG[capDef?.risk || 'low'];
                  const Icon = capDef?.domainIcon || Zap;
                  
                  return (
                    <div key={capId} className={cn(
                      "flex items-center justify-between p-2 rounded border",
                      isMissing || isUnavailable ? "border-red-500/30 bg-red-500/5" : "border-white/10 bg-black/20"
                    )}>
                      <div className="flex items-center space-x-2">
                        <Icon className={cn("w-3.5 h-3.5", capDef?.domainColor || "text-gray-400")} />
                        <span className="text-xs font-mono">{capId}</span>
                        <Badge variant="outline" className={cn("text-[7px] h-3 px-1", risk.color)}>{risk.label}</Badge>
                      </div>
                      {isMissing && <Badge variant="outline" className="text-[8px] h-4 text-red-400 border-red-500/30">NOT DECLARED</Badge>}
                      {isUnavailable && !isMissing && <Badge variant="outline" className="text-[8px] h-4 text-red-400 border-red-500/30">DENIED</Badge>}
                      {!isMissing && !isUnavailable && <CheckCircle className="w-3 h-3 text-green-400" />}
                    </div>
                  );
                })}
              </div>
            )}
            
            {(effect.missingCaps.length > 0 || effect.unavailableCaps.length > 0) && (
              <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                <div className="text-[9px] font-medium text-red-400 mb-1">EXECUTION BLOCKED</div>
                <div className="text-[10px] text-red-300">
                  Authority denied. This effect cannot execute in {runtimeProfile}.
                </div>
              </div>
            )}
          </Section>

          {/* I/O Contract */}
          <Section title="I/O Contract">
            <div className="grid grid-cols-3 gap-2">
              <Field label="Input" value={effect.input_type || 'any'} />
              <Field label="Output" value={effect.output_type || 'any'} />
              <Field label="Failure" value={effect.failure_type || 'Error'} />
            </div>
          </Section>

          {/* Execution Semantics */}
          <Section title="Execution Semantics">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Execution" value={effect.execution_type || 'async'} />
              <Field label="Determinism" value={effect.determinism || 'environment'} />
            </div>
          </Section>

          {/* Invocation Sites */}
          <Section title="Invocation Sites">
            {effect.invocations.length === 0 ? (
              <div className="text-xs text-yellow-400 p-2 bg-yellow-500/10 rounded border border-yellow-500/20">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Unreachable: defined but never invoked.
              </div>
            ) : (
              <div className="space-y-1">
                {effect.invocations.map((inv, idx) => (
                  <button key={idx} onClick={() => onNavigateToState(inv.stateId)}
                    className="w-full flex items-center justify-between p-2 rounded border border-white/10 bg-black/20 hover:bg-white/5 text-left">
                    <div className="flex items-center space-x-2">
                      <Link2 className="w-3 h-3 text-blue-400" />
                      <span className="text-xs font-mono">{inv.stateId}</span>
                      <span className="text-[9px] text-gray-500">→ {inv.type}</span>
                      {inv.event && <span className="text-[9px] text-cyan-400">[{inv.event}]</span>}
                    </div>
                    <ChevronRight className="w-3 h-3 text-gray-500" />
                  </button>
                ))}
              </div>
            )}
          </Section>
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const Section = ({ title, children }) => (
  <div>
    <div className="text-[9px] font-medium text-gray-500 uppercase tracking-wider mb-2">{title}</div>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <label className="text-[8px] text-gray-600 uppercase">{label}</label>
    <div className="mt-0.5 px-2 py-1 rounded text-xs font-mono bg-black/30 border border-white/5 text-gray-300 truncate">
      {value}
    </div>
  </div>
);

const ValidationIssue = ({ code, message, severity }) => (
  <div className={cn("flex items-start space-x-1.5 text-[9px] py-0.5", severity === 'error' ? "text-red-400" : "text-yellow-400")}>
    {severity === 'error' ? <XCircle className="w-2.5 h-2.5 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0 mt-0.5" />}
    <span><span className="font-mono">[{code}]</span> {message}</span>
  </div>
);

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getEffectsUsingCapability(machine, capId) {
  if (!machine?.effects) return [];
  return Object.entries(machine.effects)
    .filter(([_, effect]) => effect.required_capabilities?.some(c => (c.name || c) === capId))
    .map(([name]) => name);
}

function getEffectInvocations(machine, effectName) {
  if (!machine?.states) return [];
  const invocations = [];
  Object.entries(machine.states).forEach(([stateId, state]) => {
    state.entry?.forEach(e => { if ((e.effect || e) === effectName) invocations.push({ stateId, type: 'entry' }); });
    state.exit?.forEach(e => { if ((e.effect || e) === effectName) invocations.push({ stateId, type: 'exit' }); });
    state.transitions?.forEach(t => {
      t.effects?.forEach(e => { if ((e.effect || e) === effectName) invocations.push({ stateId, type: 'transition', event: t.event, target: t.target }); });
    });
  });
  return invocations;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CapabilitiesEffectsPanel = ({ machine, runtimeProfile = 'simulation', onMachineUpdate, onHighlightState }) => {
  const [selectedCapability, setSelectedCapability] = useState(null);
  const [selectedEffect, setSelectedEffect] = useState(null);

  const declaredCapabilities = useMemo(() => {
    if (!machine) return [];
    const caps = new Set();
    if (machine.effects) {
      Object.values(machine.effects).forEach(effect => {
        effect.required_capabilities?.forEach(cap => caps.add(cap.name || cap));
      });
    }
    machine.meta?.declared_capabilities?.forEach(cap => caps.add(cap));
    return Array.from(caps);
  }, [machine]);

  const handleAddCapability = useCallback((capId) => {
    if (!machine) return;
    onMachineUpdate({
      ...machine,
      meta: { ...machine.meta, declared_capabilities: [...(machine.meta?.declared_capabilities || []), capId] }
    });
  }, [machine, onMachineUpdate]);

  const handleRemoveCapability = useCallback((capId) => {
    if (!machine) return;
    onMachineUpdate({
      ...machine,
      meta: { ...machine.meta, declared_capabilities: (machine.meta?.declared_capabilities || []).filter(c => c !== capId) }
    });
  }, [machine, onMachineUpdate]);

  const handleCreateEffect = useCallback((effectDef) => {
    if (!machine) return;
    onMachineUpdate({ ...machine, effects: { ...machine.effects, [effectDef.name]: effectDef } });
    setSelectedEffect(effectDef.name);
  }, [machine, onMachineUpdate]);

  const handleDeleteEffect = useCallback((effectName) => {
    if (!machine) return;
    const { [effectName]: _, ...rest } = machine.effects || {};
    onMachineUpdate({ ...machine, effects: rest });
  }, [machine, onMachineUpdate]);

  return (
    <div className="h-full flex flex-col bg-[#0a0e1a]">
      <div className="px-4 py-2 bg-gradient-to-r from-yellow-500/10 to-transparent border-b border-yellow-500/20 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-yellow-400" />
          <h1 className="font-bold text-sm">Capabilities & Effects</h1>
          <Badge variant="outline" className="text-[9px] h-4 text-yellow-400 border-yellow-500/30">AUTHORITY</Badge>
        </div>
        <div className="flex items-center space-x-2 text-[10px] text-gray-500">
          <span>Runtime:</span>
          <Badge variant="outline" className="text-[9px] h-4">{runtimeProfile}</Badge>
        </div>
      </div>

      <CapabilityRegistry
        machine={machine}
        runtimeProfile={runtimeProfile}
        declaredCapabilities={declaredCapabilities}
        onAddCapability={handleAddCapability}
        onRemoveCapability={handleRemoveCapability}
        onSelectCapability={setSelectedCapability}
        selectedCapability={selectedCapability}
        onHighlightEffects={() => {}}
      />

      <EffectRegistry
        machine={machine}
        declaredCapabilities={declaredCapabilities}
        runtimeProfile={runtimeProfile}
        selectedEffect={selectedEffect}
        onSelectEffect={setSelectedEffect}
        onHighlightStates={(ids) => ids.forEach(id => onHighlightState?.(id))}
        onCreateEffect={handleCreateEffect}
        onUpdateEffect={() => {}}
        onDeleteEffect={handleDeleteEffect}
      />

      <div className="px-4 py-1.5 bg-black/40 border-t border-white/10 flex-shrink-0">
        <div className="flex items-center justify-between text-[8px] text-gray-600">
          <span>Compiler: all effects must be valid, all capabilities must be available</span>
          <span>Effects define law, not convenience</span>
        </div>
      </div>
    </div>
  );
};

export default CapabilitiesEffectsPanel;
